import { useImperativeHandle, useMemo, useRef } from 'react';
import { classNames } from '../../utils/classNames';
import { getActionBarConfigForEdit } from '../../utils/actionBarUtils';
import type {
  CommentListProps,
  CommentListRef,
  Comment,
  CommentMention,
  CommentAttachment
} from './CommentList.types';
import {
  useEditManager,
  useCommentMenu,
  useFormManager,
  useMostRecentMention
} from '../../hooks';

export function useCommentList(
  props: Omit<CommentListProps, 'errorBoundaryProps'>,
  ref: React.Ref<CommentListRef>
) {
  const {
    comments,
    showMenu = true,
    menuOptions = ['Edit', 'Delete', 'Resolve'],
    resolvedCommentMenuOptions = ['Unresolve', 'Delete'],
    loggedInuserEmail,
    userRole,
    handleEdit,
    handleMenuAction,
    handleReplySubmit,
    replyMenuOptions = ['Edit', 'Delete'],
    handleReplyMenuAction,
    allowReplies = true,
    isLoading = false,
    styleOverrides,
    className,
    inputConfig,
    iconOverrides,
    sectionDataKey = 'sectionCategory',
    commentOnClickHandler,
    showEditedAt,
    submitOnBlur = false,
    authorRestrictedOptions,
    adminRestrictedOptions,
    theme = 'light',
    useVirtualization,
    ...restProps
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const { isEditing, startEdit, cancelEdit, submitEdit } =
    useEditManager(handleEdit);

  useImperativeHandle(
    ref,
    () => ({
      startEdit,
      cancelEdit,
      isEditing,
      getElement: () => containerRef.current
    }),
    [startEdit, cancelEdit, isEditing]
  );

  const highlightedCommentIds = useMostRecentMention(
    comments,
    loggedInuserEmail
  );

  const { getCommentMenuOptions, getReplyMenuOptions } = useCommentMenu({
    showMenu,
    menuOptions,
    resolvedCommentMenuOptions,
    replyMenuOptions,
    loggedInuserEmail,
    authorRestrictedOptions,
    adminRestrictedOptions,
    userRole
  });

  const { getFormValue, changeFormValue, submitForm } = useFormManager(
    undefined,
    handleReplySubmit
  );

  const { unresolvedComments, resolvedComments } = useMemo(() => {
    const unresolved = comments.filter(c => c.commentStatus !== 'RESOLVED');
    const resolved = comments.filter(c => c.commentStatus === 'RESOLVED');
    return { unresolvedComments: unresolved, resolvedComments: resolved };
  }, [comments]);

  const handleCommentMenuAction = (commentId: string, optionId: string) => {
    const comment = comments.find(c => c.id === commentId);
    if (!comment) return;
    if (optionId === 'edit') {
      startEdit(
        commentId,
        comment.text,
        false,
        commentId,
        undefined,
        undefined,
        undefined,
        comment.commentMentions,
        comment.attachments
      );
    } else {
      handleMenuAction?.(optionId, comment);
    }
  };

  const handleReplyMenuActionInternal = (replyId: string, optionId: string) => {
    let targetReply: Comment | undefined;
    let parentCommentId: string | undefined;
    for (const comment of comments) {
      if (comment.replies) {
        targetReply = comment.replies.find(r => r.id === replyId);
        if (targetReply) {
          parentCommentId = comment.id;
          break;
        }
      }
    }
    if (!targetReply) return;
    if (optionId === 'edit') {
      startEdit(
        replyId,
        targetReply.text,
        false,
        parentCommentId,
        undefined,
        undefined,
        undefined,
        targetReply.commentMentions,
        targetReply.attachments
      );
    } else {
      handleReplyMenuAction?.(optionId, targetReply, parentCommentId!);
    }
  };

  const getCardPropsFor = (comment: Comment) => ({
    comment,
    loggedInUserEmail: loggedInuserEmail,
    isHighlighted: highlightedCommentIds.includes(comment.id),
    menuActions: {
      getCommentOptions: getCommentMenuOptions,
      getReplyOptions: getReplyMenuOptions,
      onCommentAction: handleCommentMenuAction,
      onReplyAction: handleReplyMenuActionInternal
    },
    editActions: {
      onTextEdit: (
        id: string,
        newText: string,
        mentions?: CommentMention[],
        attachments?: (CommentAttachment | File)[]
      ) => submitEdit(id, newText, mentions, attachments),
      onEditStart: (id: string) => {
        const mainComment = comments.find(c => c.id === id);
        if (mainComment) {
          startEdit(
            id,
            mainComment.text,
            false,
            id,
            undefined,
            undefined,
            undefined,
            mainComment.commentMentions,
            mainComment.attachments
          );
          return;
        }
        let targetReply: Comment | undefined;
        let parentCommentId: string | undefined;
        for (const c of comments) {
          if (c.replies) {
            targetReply = c.replies.find(r => r.id === id);
            if (targetReply) {
              parentCommentId = c.id;
              break;
            }
          }
        }
        if (targetReply) {
          startEdit(
            id,
            targetReply.text,
            false,
            parentCommentId,
            undefined,
            undefined,
            undefined,
            targetReply.commentMentions,
            targetReply.attachments
          );
        }
      },
      onEditCancel: cancelEdit,
      isEditing
    },
    replyActions: {
      allowReplies,
      replyValue: getFormValue(comment.id),
      onReplyChange: changeFormValue,
      onReplySubmit: (
        commentId: string,
        reply: string,
        mentions?: CommentMention[],
        attachments?: File[]
      ) => submitForm(commentId, reply, mentions, attachments),
      onClearReply: (commentId: string) => changeFormValue(commentId, '')
    },
    inputConfig: {
      ...inputConfig,
      actionBarConfig: getActionBarConfigForEdit(inputConfig?.actionBarConfig, {
        submitOnBlur,
        onCancelClick: () => changeFormValue(comment.id, '')
      }).actionBarConfig
    },
    iconOverrides,
    sectionDataKey,
    commentOnClickHandler,
    showEditedAt,
    submitOnBlur
  });

  const containerProps = {
    ref: containerRef,
    className: classNames('comment-list', className),
    style: styleOverrides,
    'data-theme': theme,
    ...restProps
  } as const;

  return {
    isLoading,
    containerProps,
    unresolvedComments,
    resolvedComments,
    getCardPropsFor
  } as const;
}
